import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Spinner,
  Modal,
  Table,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import { apiPost } from "../../service/admin/apiService";
import { showSuccess, showError } from "../../utils/toast";
import { FaPaperPlane, FaTrash, FaEdit } from "react-icons/fa";
import AdminScreenHeader from "./adminScreenHeader";
import MessageToolTip from "./messageToolTip";
import Loader from "../loader";
import Swal from "sweetalert2";
import "../../css/admin/adminMessageToUsers.css";

const AdminMessageToUsers = () => {
  const [users, setUsers] = useState([]);
  const [messageList, setMessageList] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState("");
  const [message, setMessage] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [limit, setLimit] = useState(10);
  const [activePage, setActivePage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const tk = localStorage.getItem("tk") || "";

  const filteredMessage = messageList.filter((msg) => {
    const fullMsg = `${msg.ms}`.toLowerCase();
    const term = searchTerm.toLowerCase();
    return msg.ms.toLowerCase().includes(term) || fullMsg.includes(term);
  });

  useEffect(() => {
    setShowLoader(false);
  }, [messageList]);

  useEffect(() => {
    const calculatedMaxPage = Math.ceil(filteredMessage.length / limit) || 1;
    setMaxPage(calculatedMaxPage);
    if (activePage > calculatedMaxPage) setActivePage(1);
  }, [filteredMessage, limit]);

  useEffect(() => {
    getUserList();
    fetchMessages();
  }, []);

  const getUserList = async () => {
    const payload = {
      action: "userlist",
      to: tk,
      ui: "",
    };

    if (!tk) return;

    try {
      setShowLoader(true);
      const response = await apiPost(process.env.REACT_APP_API_URL, payload);
      if (Number(response?.errordetails?.errorcode) > 0) {
        setShowLoader(false);
        showError(response?.errordetails?.error);
      } else if (response?.responsedetails) {
        setShowLoader(false);
        setUsers(response?.responsedetails || []);
      }
    } catch (err) {
      setShowLoader(false);
      showError("Something went wrong");
    }
  };

  const groupMessagesByText = (messages) => {
    console.log("msg", messages);
    const grouped = {};

    messages.forEach((msg) => {
      const key = msg.ms.trim();
      const datePart = msg.dt.split(" ")[0];

      //const user = { name: msg.nm, ir: msg.ir };

      if (!grouped[key + "|" + datePart]) {
        grouped[key + "|" + datePart] = {
          ms: msg.ms,
          dt: datePart,
          //nm: [user], //[msg.nm],
          nm: [msg.nm],
          id: [msg.id],
        };
      } else {
        grouped[key + "|" + datePart].nm.push(msg.nm);
        grouped[key + "|" + datePart].id.push(msg.id);
      }
    });
    return Object.values(grouped);
  };

  const fetchMessages = async () => {
    const payload = {
      action: "usermessagelist",
      to: tk,
      se: 1,
    };

    if (!tk) return;

    try {
      const response = await apiPost(process.env.REACT_APP_API_URL, payload);
      if (Number(response?.errordetails?.errorcode) > 0) {
        showError(response?.errordetails?.error);
        setShowLoader(false);
      } else if (response?.responsedetails) {
        const grouped =
          response?.responsedetails.length > 0
            ? groupMessagesByText(response?.responsedetails)
            : [];
        if (grouped.length > 0) {
          setMessageList(grouped);
        }
      }
    } catch (err) {
      showError("Something went wrong");
      setShowLoader(false);
    }
  };

  const sendMessage = async () => {
    if (loading) return;
    if (!selectedUserIds || !message) {
      showError("Please select user's and add message to proceed");
      return;
    }

    const payload = {
      action: "usermessageadd",
      to: tk,
      tids: selectedUserIds.map((id) => ({ id: id })),
      ms: message,
    };

    try {
      setLoading(true);
      const response = await apiPost(process.env.REACT_APP_API_URL, payload);
      if (response?.responsedetails?.success === "yes") {
        showSuccess("Message sent successfully");
        fetchMessages();
        handleCloseModal();
      } else {
        showError(response?.errordetails?.error || "Failed to send");
        setLoading(false);
      }
    } catch (error) {
      showError("Error sending message");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedUserIds("");
    setMessage("");
    setEditingMessageId(null);
  };

  const deleteMessage = async (idList) => {
    let anySuccess = false;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      width: "320px",
      customClass: {
        popup: "custom-swal-popup",
        confirmButton: "btn btn-sm btn-danger",
        cancelButton: "btn btn-sm btn-secondary",
      },
    });

    if (result.isConfirmed) {
      setShowLoader(true);
      try {
        for (const el of idList) {
          const payload = {
            action: "usermessagedelete",
            to: tk,
            id: el,
          };

          try {
            const response = await apiPost(
              process.env.REACT_APP_API_URL,
              payload
            );

            if (Number(response?.errordetails?.errorcode) > 0) {
              showError(response?.errordetails?.error);
              setShowLoader(false);
            } else if (response?.responsedetails) {
              if (response?.responsedetails?.success === "yes") {
                anySuccess = true;
              }
            }
            setShowLoader(false);
          } catch (err) {
            showError("Something went wrong");
            setShowLoader(false);
          }
        }
        if (anySuccess) {
          showSuccess("Selected message(s) deleted successfully!");
          fetchMessages();
        }
      } catch (err) {
        console.error("Error:", err);
        showError("Something went wrong");
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > maxPage) return;
    setActivePage(pageNumber);
  };

  return (
    <Container className="mt-4">
      <div className="d-none d-md-block mt-3">
        <AdminScreenHeader
          title={
            <>
              <FaPaperPlane className="me-2" />
              Admin Messages
            </>
          }
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Search message..."
          showSearch={true}
        />

        {/* Message Grid */}
        <div style={{ maxHeight: "478px", overflowY: "auto" }}>
          <Table
            striped
            bordered
            hover
            responsive
            className="w-100 custom-table text-primary custom-table-form24"
          >
            <thead className="table-light">
              <tr>
                <th style={{ width: "5%" }}></th>
                <th style={{ width: "40%" }}>Message</th>
                <th style={{ width: "40%" }}>To (Users)</th>
                <th style={{ textAlign: "center", width: "8%" }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredMessage
                .slice((activePage - 1) * limit, activePage * limit)
                .map((msg, index) => (
                  <tr key={msg.id}>
                    <td style={{ textAlign: "center" }}>
                      <FaTrash
                        style={{
                          color: "#2c3e50",
                          fontSize: "0.80rem",
                          cursor: "pointer",
                        }}
                        onClick={() => deleteMessage(msg.id)}
                      />
                    </td>
                    <td>{<MessageToolTip message={msg.ms} index={index} />}</td>
                    <td>
                      {
                        <MessageToolTip
                          message={msg.nm.join(", ")}
                          index={index}
                        />
                        // <MessageToolTip
                        //   message={msg.nm.map((user, idx) => (
                        //     <span
                        //       key={idx}
                        //       style={{
                        //         color: user.ir === 1 ? "red" : "inherit",
                        //       }}
                        //     >
                        //       {user.name}
                        //       {idx < msg.nm.length - 1 ? ", " : ""}
                        //     </span>
                        //   ))}
                        //   index={index}
                        // />
                      }
                    </td>
                    <td style={{ textAlign: "center" }}>{msg.dt}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
        <Row className="mt-3 mb-3">
          <Col className="d-flex align-items-center justify-content-between">
            {/* Records per page */}
            <div className="d-flex align-items-center">
              <label
                className="me-2 mb-0"
                style={{ color: "#0d6efd", fontSize: "13px" }}
              >
                Records per page:
              </label>
              <Form.Select
                value={limit}
                onChange={(e) => {
                  const value =
                    e.target.value === "All"
                      ? filteredMessage.length
                      : parseInt(e.target.value);
                  setLimit(value);
                  setActivePage(1); // Reset to first page on limit change
                }}
                style={{ width: "100px" }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100000">All</option>
              </Form.Select>
            </div>

            {/* Pagination */}
            <Pagination className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={activePage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(activePage - 1)}
                disabled={activePage === 1}
              />
              <Pagination.Item>
                {`Page ${activePage} of ${maxPage}`}
              </Pagination.Item>
              <Pagination.Next
                onClick={() => handlePageChange(activePage + 1)}
                disabled={activePage >= maxPage}
              />
              <Pagination.Last
                onClick={() => handlePageChange(maxPage)}
                disabled={activePage >= maxPage}
              />
            </Pagination>

            <div>
              <Button variant="primary" onClick={() => setModalVisible(true)}>
                <FaPaperPlane className="me-2" /> Create Message
              </Button>
            </div>
          </Col>
        </Row>
        {/* <div className="text-end mb-3">
          <Button variant="primary" onClick={() => setModalVisible(true)}>
            <FaPaperPlane className="me-2" /> Create Message
          </Button>
        </div> */}
      </div>

      {/* Mobile View */}
      <div className="d-block d-md-none mobile-view-container">
        <AdminScreenHeader
          title="📋 Admin Message"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Search Message..."
          showSearch={true}
        />
        <div className="scrollable-card-body">
          {messageList.map((msg, index) => (
            <Card key={msg.id} className="mb-3">
              <Card.Body>
                <Card.Text>
                  <strong>Message:</strong> {msg.ms}
                </Card.Text>
                <hr style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }} />
                <Card.Text className="card-subtitle">
                  <strong>Users:</strong> {msg.nm.join(", ")}
                </Card.Text>
                <div className="text-end">
                  <Button
                    size="sm"
                    variant="light"
                    onClick={() => deleteMessage(msg.id)}
                  >
                    <FaTrash
                      style={{
                        color: "#2c3e50",
                      }}
                    />
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
        <div className="d-flex justify-content-end mt-3">
          <Button variant="primary" onClick={() => setModalVisible(true)}>
            <FaPaperPlane className="me-2" /> Create Message
          </Button>
        </div>
      </div>

      {/* Modal for Send/Edit */}
      <Modal show={modalVisible} onHide={handleCloseModal} centered>
        <Modal.Header className="custom-header" closeButton>
          <Modal.Title>
            {editingMessageId ? "Edit Message" : "Send Message"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* ✅ Checkbox List Instead of Dropdown */}
            <Form.Group className="mb-3">
              <Form.Label>Select Users</Form.Label>
              <div className="border rounded p-2 list-background-msg">
                {users.map((user) => (
                  <Form.Check
                    key={user.ui}
                    type="checkbox"
                    label={`${user.fn} ${user.ln}`}
                    checked={selectedUserIds.includes(user.ui)}
                    onChange={() => {
                      setSelectedUserIds((prev) =>
                        prev.includes(user.ui)
                          ? prev.filter((id) => id !== user.ui)
                          : [...prev, user.ui]
                      );
                    }}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </Form.Group>

            <div className="text-end">
              <Button
                variant="primary"
                onClick={() => {
                  sendMessage();
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner className="me-2" animation="border" size="sm" />
                    Sending Message...
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="me-2" />
                    Send Message
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      {showLoader ? <Loader show={showLoader} /> : null}
    </Container>
  );
};

export default AdminMessageToUsers;
